import {
  PaginationData,
  ResourceInventoryEntry,
  ResourceInventorySummary,
  SeedBatch,
  SeedInventoryEntry,
  SeedInventorySummary,
  TrayInventoryEntry,
  TrayInventorySummary,
} from 'constants/interface';
import { CostCenterEnum, InventoryOutputTypeEnum, InventoryTypeEnum } from 'constants/inventory';
import { ResourceCategoryEnum } from 'constants/resource';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

// RESOURCE

export interface CreateOrganizationResourceInputData {
  organizationId: string
  data: {
    entryDate: string
    deliveredBy: string
    receivedBy: string
    fiscalNote?: string
    notes?: string
    items: {
      resourceId: string
      quantity: number
      price: number
    }[]
  }
}

export interface CreateOrganizationResourceOutputData {
  organizationId: string
  data: {
    entryDate: string
    costCenter: CostCenterEnum
    outputType: InventoryOutputTypeEnum
    notes?: string
    items: {
      resourceId: string
      quantity: number
    }[]
  }
}

export interface CreateOrganizationResourceTransferData {
  organizationId: string
  data: {
    entryDate: string
    sourceType: InventoryTypeEnum
    source: string
    notes?: string
    items: {
      resourceId: string
      quantity: number
      price: number
    }[]
  }
}

export interface GetOrganizationResourceInventorySummaryList extends PaginationData {
  organization?: string
  resource?: string
  category?: ResourceCategoryEnum
}

// TRAY

export interface CreateOrganizationTrayInputData {
  organizationId: string
  data: {
    entryDate: string
    deliveredBy: string
    receivedBy: string
    fiscalNote?: string
    notes?: string
    items: {
      trayId: string
      quantity: number
      price: number
    }[]
  }
}

export interface CreateOrganizationTrayOutputData {
  organizationId: string
  data: {
    entryDate: string
    costCenter: CostCenterEnum
    outputType: InventoryOutputTypeEnum
    notes?: string
    items: {
      trayId: string
      quantity: number
    }[]
  }
}

export interface CreateOrganizationTrayTransferData {
  organizationId: string
  data: {
    entryDate: string
    sourceType: InventoryTypeEnum
    source: string
    notes?: string
    items: {
      trayId: string
      quantity: number
      price: number
    }[]
  }
}

export interface GetOrganizationTrayInventorySummaryList extends PaginationData {
  organization?: string
  tray?: string
}

// VARIETY

export interface CreateOrganizationVarietyInputData {
  organizationId: string
  data: {
    entryDate: string
    deliveredBy: string
    receivedBy: string
    provider: string
    receivingTermCode: string
    fiscalNote?: string
    notes?: string
    items: {
      varietyId: string
      batch: string
      expiryDate: string
      germinationRate: number
      quantity: number
      price: number
    }[]
  }
}

export interface CreateOrganizationVarietyOutputData {
  organizationId: string
  data: {
    entryDate: string
    costCenter: CostCenterEnum
    outputType: InventoryOutputTypeEnum
    notes?: string
    items: {
      varietyId: string
      batchId: string
      quantity: number
    }[]
  }
}

export interface CreateOrganizationVarietyTransferData {
  organizationId: string
  data: {
    entryDate: string
    sourceType: InventoryTypeEnum
    source: string
    notes?: string
    items: {
      varietyId: string
      batchId: string
      quantity: number
      price: number
    }[]
  }
}

export interface GetOrganizationSeedBatchesList {
  organizationId: string
  varietyId: string
}

export interface GetOrganizationSeedInventorySummaryList extends PaginationData {
  organization?: string
  variety?: string
}

export interface OrganizationApi {
  // createOrganization(organizationData: CreateOrganizationData): Promise<Organization>
  // updateOrganization(organizationData: UpdateOrganizationData): Promise<Organization>
  // getOrganization(organizationId: string): Promise<Organization>
  // getOrganizationList(filter: PaginationData): Promise<PaginatedResponse<Organization>>
  createOrganizationResourceInput(
    resourceInputData: CreateOrganizationResourceInputData,
  ): Promise<ResourceInventoryEntry>
  createOrganizationResourceOutput(
    resourceOutputData: CreateOrganizationResourceOutputData,
  ): Promise<ResourceInventoryEntry>
  createOrganizationResourceTransfer(
    resourceTransferData: CreateOrganizationResourceTransferData,
  ): Promise<ResourceInventoryEntry>
  getResourceInventorySummaryList(
    filterData: GetOrganizationResourceInventorySummaryList,
  ): Promise<PaginatedResponse<ResourceInventorySummary>>

  createOrganizationTrayInput(
    trayInputData: CreateOrganizationTrayInputData,
  ): Promise<TrayInventoryEntry>
  createOrganizationTrayOutput(
    trayOutputData: CreateOrganizationTrayOutputData,
  ): Promise<TrayInventoryEntry>
  createOrganizationTrayTransfer(
    trayTransferData: CreateOrganizationTrayTransferData,
  ): Promise<TrayInventoryEntry>
  getTrayInventorySummaryList(
    filterData: GetOrganizationTrayInventorySummaryList,
  ): Promise<PaginatedResponse<TrayInventorySummary>>

  createOrganizationVarietyInput(
    varietyInputData: CreateOrganizationVarietyInputData,
  ): Promise<SeedInventoryEntry>
  createOrganizationVarietyOutput(
    varietyOutputData: CreateOrganizationVarietyOutputData,
  ): Promise<SeedInventoryEntry>
  createOrganizationVarietyTransfer(
    varietyTransferData: CreateOrganizationVarietyTransferData,
  ): Promise<SeedInventoryEntry>
  getSeedBatchesList(
    requestData: GetOrganizationSeedBatchesList,
  ): Promise<SeedBatch[]>
  getSeedInventorySummaryList(
    filterData: GetOrganizationSeedInventorySummaryList,
  ): Promise<PaginatedResponse<SeedInventorySummary>>
}

const organizationApiBuilder = (): OrganizationApi => {
  // const createOrganization = async (
  //   organizationData: CreateOrganizationData,
  // ): Promise<Organization> => {
  //   const params: Record<string, unknown> = {
  //     ...organizationData,
  //   };
  //   const result = await requestClient.post('/organizations', params);

  //   const createdOrganization: Organization = result.data as Organization;
  //   return createdOrganization;
  // };

  // const updateOrganization = async (
  //   organizationData: UpdateOrganizationData,
  // ): Promise<Organization> => {
  //   const { id: organizationId, data } = organizationData;
  //   const params: Record<string, unknown> = { ...data };
  //   const result = await requestClient.put(`/organizations/${organizationId}`, params);
  //   const updatedOrganization: Organization = result.data as Organization;
  //   return updatedOrganization;
  // };

  // const getOrganization = async (
  //   organizationId: string,
  // ): Promise<Organization> => {
  //   const result = await requestClient.get(`/organizations/${organizationId}`);
  //   const organization: Organization = result.data as Organization;
  //   return organization;
  // };

  // const getOrganizationList = async (
  //   filterData: PaginationData,
  // ): Promise<PaginatedResponse<Organization>> => {
  //   const params = buildPaginationParams(filterData);
  //   const result = await requestClient.get('/organizations', params);
  //   const pagination = getPaginationHeaders(result);
  //   const organizationList: Organization[] = result.data as Organization[];
  //   return {
  //     pagination,
  //     data: organizationList,
  //   };
  // };

  // RESOURCE

  const createOrganizationResourceInput = async (
    resourceInputData: CreateOrganizationResourceInputData,
  ): Promise<ResourceInventoryEntry> => {
    const { organizationId, data } = resourceInputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/resources/input`,
      params,
    );
    const createdEntry: ResourceInventoryEntry = result.data as ResourceInventoryEntry;
    return createdEntry;
  };

  const createOrganizationResourceOutput = async (
    resourceOutputData: CreateOrganizationResourceOutputData,
  ): Promise<ResourceInventoryEntry> => {
    const { organizationId, data } = resourceOutputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/resources/output`,
      params,
    );
    const createdEntry: ResourceInventoryEntry = result.data as ResourceInventoryEntry;
    return createdEntry;
  };

  const createOrganizationResourceTransfer = async (
    resourceTransferData: CreateOrganizationResourceTransferData,
  ): Promise<ResourceInventoryEntry> => {
    const { organizationId, data } = resourceTransferData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/resources/transfer`,
      params,
    );
    const createdEntry: ResourceInventoryEntry = result.data as ResourceInventoryEntry;
    return createdEntry;
  };

  const getResourceInventorySummaryList = async (
    filterData: GetOrganizationResourceInventorySummaryList,
  ): Promise<PaginatedResponse<ResourceInventorySummary>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/organizations/resources', params);
    const pagination = getPaginationHeaders(result);
    const resourceInventorySummaryList: ResourceInventorySummary[] = result
      .data as ResourceInventorySummary[];
    return {
      pagination,
      data: resourceInventorySummaryList,
    };
  };

  // TRAY

  const createOrganizationTrayInput = async (
    trayInputData: CreateOrganizationTrayInputData,
  ): Promise<TrayInventoryEntry> => {
    const { organizationId, data } = trayInputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/trays/input`,
      params,
    );
    const createdEntry: TrayInventoryEntry = result.data as TrayInventoryEntry;
    return createdEntry;
  };

  const createOrganizationTrayOutput = async (
    trayOutputData: CreateOrganizationTrayOutputData,
  ): Promise<TrayInventoryEntry> => {
    const { organizationId, data } = trayOutputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/trays/output`,
      params,
    );
    const createdEntry: TrayInventoryEntry = result.data as TrayInventoryEntry;
    return createdEntry;
  };

  const createOrganizationTrayTransfer = async (
    trayTransferData: CreateOrganizationTrayTransferData,
  ): Promise<TrayInventoryEntry> => {
    const { organizationId, data } = trayTransferData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/trays/transfer`,
      params,
    );
    const createdEntry: TrayInventoryEntry = result.data as TrayInventoryEntry;
    return createdEntry;
  };

  const getTrayInventorySummaryList = async (
    filterData: GetOrganizationTrayInventorySummaryList,
  ): Promise<PaginatedResponse<TrayInventorySummary>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/organizations/trays', params);
    const pagination = getPaginationHeaders(result);
    const trayInventorySummaryList: TrayInventorySummary[] = result
      .data as TrayInventorySummary[];
    return {
      pagination,
      data: trayInventorySummaryList,
    };
  };

  // VARIETY

  const createOrganizationVarietyInput = async (
    varietyInputData: CreateOrganizationVarietyInputData,
  ): Promise<SeedInventoryEntry> => {
    const { organizationId, data } = varietyInputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/varieties/input`,
      params,
    );
    const createdEntry: SeedInventoryEntry = result.data as SeedInventoryEntry;
    return createdEntry;
  };

  const createOrganizationVarietyOutput = async (
    varietyOutputData: CreateOrganizationVarietyOutputData,
  ): Promise<SeedInventoryEntry> => {
    const { organizationId, data } = varietyOutputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/varieties/output`,
      params,
    );
    const createdEntry: SeedInventoryEntry = result.data as SeedInventoryEntry;
    return createdEntry;
  };

  const createOrganizationVarietyTransfer = async (
    varietyTransferData: CreateOrganizationVarietyTransferData,
  ): Promise<SeedInventoryEntry> => {
    const { organizationId, data } = varietyTransferData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/organizations/${organizationId}/varieties/transfer`,
      params,
    );
    const createdEntry: SeedInventoryEntry = result.data as SeedInventoryEntry;
    return createdEntry;
  };

  const getSeedBatchesList = async (
    requestData: GetOrganizationSeedBatchesList,
  ): Promise<SeedBatch[]> => {
    const { varietyId, organizationId } = requestData;
    const result = await requestClient.get(`organizations/${organizationId}/varieties/${varietyId}`);
    const resultData = result.data as SeedBatch[];
    return resultData;
  };

  const getSeedInventorySummaryList = async (
    filterData: GetOrganizationSeedInventorySummaryList,
  ): Promise<PaginatedResponse<SeedInventorySummary>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/organizations/seeds', params);
    const pagination = getPaginationHeaders(result);
    const varietyInventorySummaryList: SeedInventorySummary[] = result
      .data as SeedInventorySummary[];
    return {
      pagination,
      data: varietyInventorySummaryList,
    };
  };

  return {
    // createOrganization,
    // updateOrganization,
    // getOrganization,
    // getOrganizationList,
    createOrganizationResourceInput,
    createOrganizationResourceOutput,
    createOrganizationResourceTransfer,
    getResourceInventorySummaryList,

    createOrganizationTrayInput,
    createOrganizationTrayOutput,
    createOrganizationTrayTransfer,
    getTrayInventorySummaryList,

    createOrganizationVarietyInput,
    createOrganizationVarietyOutput,
    createOrganizationVarietyTransfer,
    getSeedBatchesList,
    getSeedInventorySummaryList,
  };
};

export const organizationApi = organizationApiBuilder();
